import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";

export type Option = {
  value: string;
  label: string;
};

interface MultiSelectProps {
  options: Option[];
  value?: Option[];
  defaultValue?: Option[];
  onChange: (selectedOptions: Option[]) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Max badges shown before collapsing into "+N more". Defaults to showing all. */
  maxVisibleBadges?: number;
}

const encodeValue = (value: string, label: string) => `${label}|${value}`;
const decodeValue = (value: string) => value.split("|").at(-1) as string;

export function MultiSelect({
  options,
  value,
  defaultValue = [],
  onChange,
  className,
  placeholder = "Select options...",
  disabled = false,
  maxVisibleBadges,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [internalSelected, setInternalSelected] =
    React.useState<Option[]>(defaultValue);
  const selected = value ?? internalSelected;

  const updateSelected = (nextSelected: Option[]) => {
    if (value === undefined) {
      setInternalSelected(nextSelected);
    }
    onChange(nextSelected);
  };

  const visibleSelected =
    maxVisibleBadges === undefined
      ? selected
      : selected.slice(0, maxVisibleBadges);
  const hiddenCount = selected.length - visibleSelected.length;

  const handleSelect = (currentValue: string) => {
    if (disabled) return;
    const decodedValue = decodeValue(currentValue);
    const option = options.find((opt) => opt.value === decodedValue);
    if (!option) return;

    const updatedSelected = selected.some((item) => item.value === option.value)
      ? selected.filter((item) => item.value !== option.value)
      : [...selected, option];
    updateSelected(updatedSelected);
  };

  const handleRemove = (option: Option) => {
    if (disabled) return;
    const updatedSelected = selected.filter(
      (item) => item.value !== option.value,
    );
    updateSelected(updatedSelected);
  };

  const handleClear = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    updateSelected([]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "!h-auto min-h-10 w-full items-center justify-between gap-2 whitespace-normal rounded-md border border-input bg-background px-3 py-2 text-sm font-normal ring-offset-background hover:bg-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
        >
          <div className="flex max-h-[120px] min-w-0 flex-1 flex-wrap items-center gap-1.5 overflow-y-auto text-left">
            {selected.length === 0 ? (
              <span className="truncate text-muted-foreground">
                {placeholder}
              </span>
            ) : (
              <>
                {visibleSelected.map((option) => (
                  <Badge
                    key={option.value}
                    variant="secondary"
                    className="max-w-[160px] gap-1 rounded-md px-2 py-0.5 text-xs font-normal"
                  >
                    <span className="truncate">{option.label}</span>
                    <span
                      className="rounded-sm opacity-70 hover:opacity-100"
                      role="button"
                      tabIndex={disabled ? -1 : 0}
                      aria-label={`Remove ${option.label}`}
                      onKeyDown={(event) => {
                        if (
                          !disabled &&
                          (event.key === "Enter" || event.key === " ")
                        ) {
                          event.preventDefault();
                          event.stopPropagation();
                          handleRemove(option);
                        }
                      }}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleRemove(option);
                      }}
                    >
                      <X className="size-3" />
                    </span>
                  </Badge>
                ))}
                {hiddenCount > 0 && (
                  <Badge
                    variant="secondary"
                    className="rounded-md px-2 py-0.5 text-xs font-normal"
                  >
                    +{hiddenCount} more
                  </Badge>
                )}
              </>
            )}
          </div>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command>
          <CommandInput
            placeholder="Search options..."
            className="border-none focus:ring-0"
          />
          <CommandList>
            <CommandEmpty>No option found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selected.some(
                  (item) => item.value === option.value,
                );
                return (
                  <CommandItem
                    key={option.value}
                    value={encodeValue(option.value, option.label)}
                    onSelect={handleSelect}
                    disabled={disabled}
                    className="text-sm"
                  >
                    <Check
                      className={cn(
                        "mr-2 size-4",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {option.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selected.length > 0 && (
              <div className="border-t p-1">
                <Button
                  variant="ghost"
                  onClick={handleClear}
                  disabled={disabled}
                  className="h-8 w-full justify-center text-sm font-normal"
                >
                  Clear all
                </Button>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
